/**
 * robustFetch — Fetch with retry + exponential backoff + adaptive timeout.
 *
 * Designed for weak networks (2G / 3G / rural Africa) where sporadic packet
 * loss and slow TCP handshakes are normal. Never gives up on the first blip.
 *
 * Usage:
 *   const res = await robustFetch(url, { method: 'POST', body: fd });
 *   // Throws on final failure; caller decides what UX to show.
 */

/**
 * Rough network quality estimate (uses Network Information API when present).
 * Returns one of: 'slow-2g' | '2g' | '3g' | '4g' | 'unknown'
 */
export function getNetworkQuality() {
  const conn =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;
  if (!conn) return "unknown";
  return conn.effectiveType || "unknown";
}

/** Is the current network known to be slow? */
export function isSlowNetwork() {
  const q = getNetworkQuality();
  return q === "slow-2g" || q === "2g" || q === "3g";
}

/**
 * Base timeout per attempt in ms — adapts to network quality.
 */
function baseTimeoutMs() {
  const q = getNetworkQuality();
  if (q === "slow-2g") return 90000; // 90s
  if (q === "2g") return 75000;      // 75s
  if (q === "3g") return 60000;      // 60s
  return 45000;                      // default 45s
}

/**
 * Sleep helper.
 */
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Fetch with retry + timeout.
 * @param {string} url
 * @param {RequestInit & { retries?: number, timeoutMs?: number, onRetry?: (attempt, err) => void }} opts
 */
export async function robustFetch(url, opts = {}) {
  const {
    retries = 4,
    timeoutMs,
    onRetry,
    ...fetchOpts
  } = opts;

  const effectiveTimeout = timeoutMs || baseTimeoutMs();
  let lastError = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const to = setTimeout(() => controller.abort(), effectiveTimeout);

    try {
      const res = await fetch(url, {
        ...fetchOpts,
        signal: controller.signal,
      });
      clearTimeout(to);

      // 5xx = transient -> retry. 4xx = don't retry.
      if (res.status >= 500 && res.status < 600 && attempt < retries) {
        lastError = new Error(`HTTP ${res.status}`);
        if (onRetry) onRetry(attempt + 1, lastError);
        const backoff = Math.min(15000, 500 * 2 ** attempt + Math.random() * 400);
        await sleep(backoff);
        continue;
      }
      return res;
    } catch (err) {
      clearTimeout(to);
      lastError = err;

      // Abort (timeout) or network failure -> retry.
      const retriable =
        err.name === "AbortError" ||
        err.name === "TypeError" || // "Failed to fetch"
        err.message?.toLowerCase().includes("network") ||
        err.message?.toLowerCase().includes("failed");

      if (attempt < retries && retriable) {
        if (onRetry) onRetry(attempt + 1, err);
        const backoff = Math.min(15000, 500 * 2 ** attempt + Math.random() * 400);
        await sleep(backoff);
        continue;
      }
      throw err;
    }
  }
  throw lastError || new Error("robustFetch: all attempts failed");
}
